// Cognito mutates its cache before invoking the refresh callback. Stage those
// mutations so an obsolete response cannot affect another account or logout.
export function createRefreshSessionStorage(
  backing: Storage, clientId: string, username: string, refreshToken: string, isCurrent: () => boolean,
) {
  const prefix = `CognitoIdentityServiceProvider.${clientId}.`;
  const lastKey = `${prefix}LastAuthUser`;
  const originalLastUser = backing.getItem(lastKey);
  // Cognito's canonical username can differ from the email used to sign in.
  // Preserve it/device data only when its cached refresh token is this session's.
  const owner = originalLastUser && backing.getItem(`${prefix}${originalLastUser}.refreshToken`) === refreshToken
    ? originalLastUser : username;
  const ownerPrefix = `${prefix}${owner}.`;
  const keys = new Set([lastKey, ...['idToken', 'accessToken', 'refreshToken', 'clockDrift',
    'deviceKey', 'randomPasswordKey', 'deviceGroupKey', 'userData'].map(suffix => `${ownerPrefix}${suffix}`)]);
  const allowed = (key: string) => keys.has(key);
  const snapshot = new Map([...keys].map(key => [key, backing.getItem(key)]));
  const changes = new Map<string, string | null>();
  let committed = false;
  return {
    storage: {
      getItem(key: string): string | null {
        if (!allowed(key)) return null;
        if (changes.has(key)) return changes.get(key) ?? null;
        if (key === lastKey) return owner;
        return snapshot.get(key) ?? null;
      },
      setItem(key: string, value: string) {
        if (allowed(key) && (key !== lastKey || value === owner)) changes.set(key, value);
      },
      removeItem(key: string) { if (allowed(key)) changes.set(key, null); },
      clear() { snapshot.forEach((_value, key) => changes.set(key, null)); },
    },
    commit(): boolean {
      if (committed) return true;
      if (!isCurrent()) return false;
      // Another SDK login/refresh may update its cache before app state changes.
      for (const key of new Set([...snapshot.keys(), ...changes.keys()])) {
        if (backing.getItem(key) !== (snapshot.get(key) ?? null)) return false;
      }
      changes.forEach((value, key) => {
        if (value === null) backing.removeItem(key);
        else backing.setItem(key, value);
      });
      committed = true;
      return true;
    },
  };
}
